import {
  ErrorCodes,
  endpoints,
  type EndpointBody,
  type EndpointKey,
  type EndpointParams,
  type EndpointQuery,
  type EndpointResponse,
  type ErrorCode,
} from '@missions/types';

/* ============================================================================
   Typed API client driven by the endpoint manifest.
     const api = createApiClient({ baseUrl, refreshKey: 'auth.refresh' });
     const m = await api.call('missions.get', { params: { id } });
   - access token kept in memory only; refresh cookie (httpOnly) sent with credentials:'include'
   - 401 → single-flight refresh → retry once → onUnauthenticated()
   - X-Request-Id on every request; errors normalised to ApiError
   ========================================================================== */

export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode;
  readonly details: Record<string, unknown> | undefined;
  readonly requestId: string;

  constructor(init: {
    status: number;
    code: ErrorCode;
    message: string;
    details?: Record<string, unknown>;
    requestId: string;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.details = init.details;
    this.requestId = init.requestId;
  }

  /** Field errors from `validation_failed` (`details.fields`), keyed by field path. */
  get fieldErrors(): Record<string, string> {
    const f = this.details?.['fields'];
    return f && typeof f === 'object' ? (f as Record<string, string>) : {};
  }
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;

type Need<T, K extends string> = undefined extends T ? { [P in K]?: T } : { [P in K]: T };

/** Input for a call: params/query/body are required only when the endpoint declares them. */
export type CallInput<K extends EndpointKey> = Need<EndpointParams<K>, 'params'> &
  Need<EndpointQuery<K>, 'query'> &
  Need<EndpointBody<K>, 'body'> & { signal?: AbortSignal; headers?: Record<string, string> };

/** True when every part of the input is optional, so the input arg itself can be omitted. */
type AllOptional<K extends EndpointKey> =
  undefined extends EndpointParams<K>
    ? undefined extends EndpointQuery<K>
      ? undefined extends EndpointBody<K>
        ? true
        : false
      : false
    : false;

export type CallArgs<K extends EndpointKey> =
  AllOptional<K> extends true ? [input?: CallInput<K>] : [input: CallInput<K>];

export interface ApiClientOptions {
  /** e.g. https://api.missions.devninjaweb.com (no trailing slash needed) */
  baseUrl: string;
  /** endpoint used to refresh the access token: tenant apps 'auth.refresh', Mission HQ 'hq.auth.refresh' */
  refreshKey?: 'auth.refresh' | 'hq.auth.refresh';
  /** extra headers on every request (tenant host/slug hints) */
  headers?: () => Record<string, string>;
  /** called when refresh fails → app shows the sign-in screen */
  onUnauthenticated?: () => void;
  /** called whenever the access token changes (null = signed out) */
  onTokenChange?: (token: string | null) => void;
  /** validate responses against the manifest schema (dev/test). Default false. */
  validateResponses?: boolean;
  fetch?: typeof fetch;
}

const newRequestId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/** Replaces :params in a path. Throws if one is missing. */
export function buildPath(path: string, params: Record<string, unknown> | undefined): string {
  return path.replace(/:([A-Za-z0-9_]+)/g, (_m, name: string) => {
    const v = params?.[name];
    if (v === undefined || v === null || v === '') throw new Error(`Missing path param "${name}" for ${path}`);
    return encodeURIComponent(String(v));
  });
}

/** Serialises a query object: skips undefined/null, repeats arrays, JSON-encodes nested objects. */
export function buildQuery(query: Record<string, unknown> | undefined): string {
  if (!query) return '';
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    if (Array.isArray(v)) v.forEach((x) => sp.append(k, String(x)));
    else if (typeof v === 'object') sp.append(k, JSON.stringify(v));
    else sp.append(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

const KNOWN_CODES = new Set<string>(ErrorCodes);
const statusCode = (status: number): ErrorCode =>
  status === 400
    ? 'bad_request'
    : status === 401
      ? 'unauthenticated'
      : status === 403
        ? 'forbidden'
        : status === 404
          ? 'not_found'
          : status === 409
            ? 'conflict'
            : status === 413
              ? 'payload_too_large'
              : status === 415
                ? 'unsupported_media_type'
                : status === 422
                  ? 'validation_failed'
                  : status === 429
                    ? 'rate_limited'
                    : status === 503
                      ? 'service_unavailable'
                      : 'internal';

async function toApiError(res: Response, requestId: string): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* not JSON */
  }
  const err = (
    body as {
      error?: { code?: string; message?: string; details?: Record<string, unknown>; requestId?: string };
    } | null
  )?.error;
  const code = err?.code && KNOWN_CODES.has(err.code) ? (err.code as ErrorCode) : statusCode(res.status);
  return new ApiError({
    status: res.status,
    code,
    message: err?.message ?? (res.statusText || 'Request failed'),
    details: err?.details,
    requestId: err?.requestId ?? res.headers.get('x-request-id') ?? requestId,
  });
}

export interface ApiClient {
  call<K extends EndpointKey>(key: K, ...args: CallArgs<K>): Promise<EndpointResponse<K>>;
  /** Build the full URL for an endpoint (e.g. EventSource for streams). */
  url<K extends EndpointKey>(
    key: K,
    input?: { params?: Record<string, unknown>; query?: Record<string, unknown> },
  ): string;
  getAccessToken(): string | null;
  setAccessToken(token: string | null): void;
  /** Force a refresh now (app boot): resolves true when signed in. */
  refresh(): Promise<boolean>;
}

export function createApiClient(opts: ApiClientOptions): ApiClient {
  const base = opts.baseUrl.replace(/\/$/, '');
  const doFetch = opts.fetch ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  let token: string | null = null;
  let refreshing: Promise<boolean> | null = null;

  const setAccessToken = (t: string | null): void => {
    token = t;
    opts.onTokenChange?.(t);
  };

  const rawRequest = async (
    key: EndpointKey,
    input: CallInput<EndpointKey> | undefined,
    withAuth: boolean,
  ): Promise<Response> => {
    const def = endpoints[key];
    const i = (input ?? {}) as {
      params?: Record<string, unknown>;
      query?: Record<string, unknown>;
      body?: unknown;
      signal?: AbortSignal;
      headers?: Record<string, string>;
    };
    const requestId = newRequestId();
    const headers: Record<string, string> = {
      accept: 'application/json',
      'x-request-id': requestId,
      ...(opts.headers?.() ?? {}),
      ...(i.headers ?? {}),
    };
    if (i.body !== undefined) headers['content-type'] = 'application/json';
    if (withAuth && token) headers['authorization'] = `Bearer ${token}`;
    const url = `${base}${buildPath(def.path, i.params)}${buildQuery(i.query)}`;
    try {
      return await doFetch(url, {
        method: def.method,
        headers,
        body: i.body === undefined ? undefined : JSON.stringify(i.body),
        credentials: 'include',
        signal: i.signal,
      });
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') throw e;
      throw new ApiError({
        status: 0,
        code: 'service_unavailable',
        message: 'Network error: check your connection and try again.',
        requestId,
      });
    }
  };

  const refresh = (): Promise<boolean> => {
    if (!opts.refreshKey) return Promise.resolve(false);
    // single-flight: concurrent 401s share one refresh
    refreshing ??= (async () => {
      try {
        const res = await rawRequest(opts.refreshKey as EndpointKey, undefined, false);
        if (!res.ok) {
          setAccessToken(null);
          return false;
        }
        const j = (await res.json()) as { accessToken?: string };
        setAccessToken(j.accessToken ?? null);
        return Boolean(j.accessToken);
      } catch {
        setAccessToken(null);
        return false;
      } finally {
        queueMicrotask(() => {
          refreshing = null;
        });
      }
    })();
    return refreshing;
  };

  async function call<K extends EndpointKey>(key: K, ...args: CallArgs<K>): Promise<EndpointResponse<K>> {
    const def = endpoints[key];
    const input = args[0] as CallInput<EndpointKey> | undefined;
    const authed = def.auth === 'tenant' || def.auth === 'platform';
    let res = await rawRequest(key, input, authed);
    if (res.status === 401 && authed && key !== opts.refreshKey) {
      const ok = await refresh();
      if (ok) res = await rawRequest(key, input, true);
      if (!ok || res.status === 401) {
        opts.onUnauthenticated?.();
        throw await toApiError(res, 'refresh-failed');
      }
    }
    if (!res.ok) throw await toApiError(res, res.headers.get('x-request-id') ?? '');
    const json: unknown = res.status === 204 ? { ok: true } : await res.json();
    // login / accept / claim / setup / totp return a session: keep the access token
    if (
      json &&
      typeof json === 'object' &&
      'accessToken' in json &&
      typeof (json as { accessToken: unknown }).accessToken === 'string'
    ) {
      setAccessToken((json as { accessToken: string }).accessToken);
    }
    if (opts.validateResponses) {
      const parsed = def.response.safeParse(json);
      if (!parsed.success) {
        console.error(`[api-client] ${key} response does not match the contract`, parsed.error.flatten());
      }
    }
    if (key === 'auth.logout' || key === 'hq.auth.logout') setAccessToken(null);
    return json as EndpointResponse<K>;
  }

  return {
    call,
    url: (key, input) => `${base}${buildPath(endpoints[key].path, input?.params)}${buildQuery(input?.query)}`,
    getAccessToken: () => token,
    setAccessToken,
    refresh,
  };
}
